import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Feather } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { n: "01", title: "Dream it", body: "Shape your idea into a story children will love — characters, message and heart." },
  { n: "02", title: "Write it", body: "Story craft for young readers: rhythm, read-aloud flow and words that work." },
  { n: "03", title: "Picture it", body: "Illustration, layout and design — how words and pictures sing together." },
  { n: "04", title: "Publish it", body: "Print, ISBNs and getting your physical book onto Amazon and into shops." },
  { n: "05", title: "Share it", body: "Schools, libraries and events, building your life as a published author." },
];

export default function Writers() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, message: `Writer's journey enquiry: ${form.message}`, type: "writer-journey" });
      toast.success("Enquiry sent — Damali will be in touch about your writer's journey.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const input = "w-full rounded-xl border border-cream/25 bg-cream/10 px-4 py-3 text-sm text-cream placeholder:text-cream/50 focus:outline-none focus:ring-2 focus:ring-cream/50 transition";

  return (
    <section id="writers" data-testid="writers-section" className="py-24 sm:py-32 bg-sage text-cream relative overflow-hidden">
      <div className="absolute w-[30rem] h-[30rem] rounded-full bg-cream/10 blur-[110px] -top-24 -left-24 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full bg-cream/15 border border-cream/25 px-4 py-1.5 mb-6">
                <Feather size={14} />
                <span className="text-[11px] font-bold uppercase tracking-[0.2em]">New</span>
              </div>
              <Eyebrow className="mb-4 !text-cream/80">For budding Book Authors!</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-5">
                Your story deserves to be told
              </h2>
              <p className="text-sm sm:text-base text-cream/85 leading-relaxed max-w-xl mb-4">
                Five years ago Damali had an idea and a blank page. Today she's a
                five-times published author whose books are read in homes and classrooms
                across the UK and now she helps other writers make the same journey.
              </p>
              <p className="text-sm sm:text-base text-cream/85 leading-relaxed max-w-xl mb-10">
                If you've ever dreamed of writing a children's book, this is where your
                writer's journey begins: friendly, practical guidance from someone who
                has walked every step from first idea to published book.
              </p>
            </Reveal>

            <div className="flex flex-col">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.06}>
                  <div
                    data-testid={`writer-step-${s.n}`}
                    className="group grid sm:grid-cols-[auto_1fr] gap-4 sm:gap-7 py-6 border-t border-cream/20 last:border-b items-start"
                  >
                    <span className="font-mono font-bold text-3xl sm:text-4xl text-cream/50 group-hover:text-cream transition-colors leading-none">
                      {s.n}
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight mb-1.5">{s.title}</h3>
                      <p className="text-sm text-cream/75 leading-relaxed max-w-lg">{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15} className="lg:sticky lg:top-28">
              <form
                data-testid="writers-enquiry-form"
                onSubmit={submit}
                className="bg-cream text-walnut rounded-[1.75rem] p-7 sm:p-9 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.45)]"
              >
                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight mb-1.5">Start your writer's journey</h3>
                <p className="text-sm text-walnutsoft mb-6">
                  Tell Damali where you are with your story — even if it's just an idea —
                  and she'll reply with how she can help.
                </p>
                <div className="flex flex-col gap-3 mb-3">
                  <input
                    data-testid="writers-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className="w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition"
                  />
                  <input
                    data-testid="writers-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Your email address"
                    className="w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition"
                  />
                  <textarea
                    data-testid="writers-message-input"
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Your story idea, where you're stuck, or what you dream of publishing…"
                    className="w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition resize-none"
                  />
                </div>
                <button
                  data-testid="writers-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group w-full rounded-full bg-walnut text-cream py-4 font-display font-bold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-terracotta transition-colors disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Begin the journey"}
                  <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
